import axios from 'axios'

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api/v1',
  timeout: 30000,
})

// Request interceptor - attach token
api.interceptors.request.use((config) => {
  const auth = JSON.parse(localStorage.getItem('skud-auth') || '{}')
  const token = auth?.state?.token
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Response interceptor - handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config
    if (error.response?.status === 401 && !original._retry) {
      original._retry = true
      try {
        const auth = JSON.parse(localStorage.getItem('skud-auth') || '{}')
        const refreshToken = auth?.state?.refreshToken
        if (!refreshToken) throw new Error('No refresh token')

        const resp = await axios.post(`${original.baseURL || '/api/v1'}/auth/token/refresh/`, {
          refresh: refreshToken,
        })
        const newToken = resp.data.access

        // Update store
        const stored = JSON.parse(localStorage.getItem('skud-auth') || '{}')
        stored.state.token = newToken
        localStorage.setItem('skud-auth', JSON.stringify(stored))

        original.headers.Authorization = `Bearer ${newToken}`
        return api(original)
      } catch (e) {
        localStorage.removeItem('skud-auth')
        window.location.href = '/login'
        return Promise.reject(e)
      }
    }
    return Promise.reject(error)
  }
)

export default api

// Typed service modules
export const authService = {
  login: (data) => api.post('/auth/token/', data),
  me: () => api.get('/auth/users/me/'),
  changePassword: (data) => api.post('/auth/users/change_password/', data),
}

export const studentService = {
  list: (params) => api.get('/students/', { params }),
  get: (id) => api.get(`/students/${id}/`),
  create: (data) => api.post('/students/', data),
  update: (id, data) => api.patch(`/students/${id}/`, data),
  delete: (id) => api.delete(`/students/${id}/`),
  bulkDelete: (data, params) => api.post('/students/bulk-destroy/', data, { params }),
  importExcel: (file) => {
    const fd = new FormData()
    fd.append('file', file)
    return api.post('/students/import-excel/', fd)
  },
  bulkImport: (excelFile, zipFile) => {
    const fd = new FormData()
    fd.append('excel_file', excelFile)
    if (zipFile) fd.append('zip_file', zipFile)
    return api.post('/students/bulk-import/', fd, { timeout: 600000 }) // 10 minutes
  },
  exportExcel: (params) => api.get('/students/export-excel/', { params, responseType: 'blob' }),
  registerDevice: (id, deviceId) => api.post(`/students/${id}/register-device/`, { device_id: deviceId }),
  bulkRegisterDevice: (data) => api.post('/students/bulk-register-device/', data),
  stats: (id, params) => api.get(`/students/${id}/stats/`, { params }),
}

export const employeeService = {
  list: (params) => api.get('/employees/', { params }),
  get: (id) => api.get(`/employees/${id}/`),
  create: (data) => api.post('/employees/', data),
  update: (id, data) => api.patch(`/employees/${id}/`, data),
  delete: (id) => api.delete(`/employees/${id}/`),
  bulkDelete: (data, params) => api.post('/employees/bulk-destroy/', data, { params }),
  importExcel: (file) => {
    const fd = new FormData()
    fd.append('file', file)
    return api.post('/employees/import-excel/', fd)
  },
  bulkImport: (excelFile, zipFile) => {
    const fd = new FormData()
    fd.append('excel_file', excelFile)
    if (zipFile) fd.append('zip_file', zipFile)
    return api.post('/employees/bulk-import/', fd, { timeout: 600000 }) // 10 minutes
  },
  exportExcel: (params) => api.get('/employees/export-excel/', { params, responseType: 'blob' }),
  downloadTemplate: () => api.get('/employees/download-template/', { responseType: 'blob' }),
  registerDevice: (id, deviceId) => api.post(`/employees/${id}/register-device/`, { device_id: deviceId }),
  bulkRegisterDevice: (data) => api.post('/employees/bulk-register-device/', data),
  stats: (id, params) => api.get(`/employees/${id}/stats/`, { params }),
}

export const attendanceService = {
  logs: (params) => api.get('/attendance/logs/', { params }),
  today: () => api.get('/attendance/logs/today/'),
  personHistory: (params) => api.get('/attendance/logs/person-history/', { params }),
  exportExcel: (params) => api.get('/attendance/logs/export-excel/', { params, responseType: 'blob' }),
}

export const analyticsService = {
  dashboard: () => api.get('/analytics/dashboard/'),
  dashboardStatDetail: (params) => api.get('/analytics/dashboard-stat/', { params }),
  activityFlow: () => api.get('/analytics/activity-flow/'),
  trend: (params) => api.get('/analytics/trend/', { params }),
  classes: (params) => api.get('/analytics/groups/', { params }),
  departments: (params) => api.get('/analytics/departments/', { params }),
  person: (params) => api.get('/analytics/person/', { params }),
  departmentDetail: (id, params) => api.get(`/analytics/departments/${id}/`, { params }),
  departmentHierarchyStats: (id, params) => api.get(`/analytics/departments/${id}/hierarchy-stats/`, { params }),
  directionHierarchyStats: (id, params) => api.get(`/analytics/directions/${id}/hierarchy-stats/`, { params }),
  classDetail: (id, params) => api.get(`/analytics/groups/${id}/`, { params }),
  employeeDeptDetail: (id, params) => api.get(`/analytics/employees-departments/${id}/`, { params }),

  // Local search — scoped to a single structural unit
  classStudents: (id, params) => api.get(`/analytics/groups/${id}/students/`, { params }),
  employeeDeptEmployees: (id, params) => api.get(`/analytics/employee-departments/${id}/employees/`, { params }),

  // Structure-level Excel exports
  exportClassExcel: (id, params) =>
    api.get(`/analytics/groups/${id}/export/excel/`, { params, responseType: 'blob' }),
  exportEmpDeptExcel: (id, params) =>
    api.get(`/analytics/employee-departments/${id}/export/excel/`, { params, responseType: 'blob' }),
}

export const scheduleService = {
  list: (params) => api.get('/schedules/', { params }),
  get: (id) => api.get(`/schedules/${id}/`),
  create: (data) => api.post('/schedules/', data),
  update: (id, data) => api.patch(`/schedules/${id}/`, data),
  delete: (id) => api.delete(`/schedules/${id}/`),
}

export const deviceService = {
  list: (params) => api.get('/devices/', { params }),
  get: (id) => api.get(`/devices/${id}/`),
  create: (data) => api.post('/devices/', data),
  update: (id, data) => api.patch(`/devices/${id}/`, data),
  ping: (id) => api.post(`/devices/${id}/ping/`),
  info: (id) => api.get(`/devices/${id}/info/`),
  syncUsers: (id, force = false) => api.post(`/devices/${id}/sync-users/`, { force }),
  bulkSync: (deviceIds, force = false, personType = null) => api.post('/devices/bulk-sync/', {
    device_ids: deviceIds,
    force,
    ...(personType ? { person_type: personType } : {}),
  }),
  openDoor: (id, doorNo = 1) => api.post(`/devices/${id}/open-door/`, { door_no: doorNo }),
  applyWorkingHours: (id, data) => api.post(`/devices/${id}/apply-working-hours/`, data),
  toggleBlock: (id) => api.post(`/devices/${id}/toggle-block/`),
}

export const userService = {
  list: (params) => api.get('/auth/users/', { params }),
  get: (id) => api.get(`/auth/users/${id}/`),
  create: (data) => api.post('/auth/users/', data),
  update: (id, data) => api.patch(`/auth/users/${id}/`, data),
  delete: (id) => api.delete(`/auth/users/${id}/`),
  roles: () => api.get('/auth/roles/'),
  resetPassword: (id, data) => api.post(`/auth/users/${id}/reset_password/`, data),
}

export const departmentService = {
  list: (params) => api.get('/departments/departments/', { params }),
  create: (data) => api.post('/departments/departments/', data),
  update: (id, data) => api.patch(`/departments/departments/${id}/`, data),
  delete: (id) => api.delete(`/departments/departments/${id}/`),
  hierarchy: () => api.get('/departments/departments/hierarchy/'),

  directions: (params) => api.get('/departments/directions/', { params }),
  createDirection: (data) => api.post('/departments/directions/', data),
  updateDirection: (id, data) => api.patch(`/departments/directions/${id}/`, data),
  deleteDirection: (id) => api.delete(`/departments/directions/${id}/`),

  classes: (params) => api.get('/departments/classes/', { params }),
  createClass: (data) => api.post('/departments/classes/', data),
  updateClass: (id, data) => api.patch(`/departments/classes/${id}/`, data),
  deleteClass: (id) => api.delete(`/departments/classes/${id}/`),
  importClassesExcel: (file) => {
    const fd = new FormData()
    fd.append('file', file)
    return api.post('/departments/classes/import-excel/', fd)
  },

  positions: (params) => api.get('/departments/positions/', { params }),
  createPosition: (data) => api.post('/departments/positions/', data),
  updatePosition: (id, data) => api.patch(`/departments/positions/${id}/`, data),
  deletePosition: (id) => api.delete(`/departments/positions/${id}/`),
}
